/**
 * @prettier
 */

import React, { FunctionComponent } from "react";
import { Router, RouteComponentProps, Redirect } from "@reach/router";

import "react-toastify/dist/ReactToastify.css";
import App from "App";
import Dashboard from "containers/Dashboard";
import { UserProvider } from "UserContext";
import NotFound from "components/NotFound";
import PrivateRoute from "hoc/PrivateRoute";
import ErrorBoundary from "components/ErrorBoundary";
import { UnsavedProvider } from "LinkContext";
// mass emailer hidden 2026-10-14 (George: not used) - code kept for a later clean removal
// import { MassEmailer } from "containers/MassEmailer/MassEmailer";
// mass emailer hidden 2026-10-14 (George: not used) - code kept for a later clean removal
// import { CreateCampaign as EmailCampaignCreation } from "components/MassEmailer/CreateCampaign";
// mass emailer hidden 2026-10-14 (George: not used) - code kept for a later clean removal
// import { CampaignReport } from "containers/MassEmailer/CampaignReport";
import { ToastContainer } from "react-toastify";
// import { Insight } from "containers/AiReport/Insight";
// import { GeneratedInsight } from "containers/AiReport/GeneratedInsight";

import { lazyPage, named } from "utils/lazyPage";

// pages load on first visit (see utils/lazyPage)
const AuthPage = lazyPage(() => import("containers/AuthPage"));
const UserManagementPage = lazyPage(() => import("containers/UserManagement"));
const AdminSettings = lazyPage(() => import("containers/AdminSettings"));
const ClientInfoPage = lazyPage(() => import("containers/ClientInfo"));
const BeMeasured = lazyPage(() => import("containers/BeMeasured"));
const HealthCheckPage = lazyPage(() => import("containers/HealthCheck"));
const SpotifyStatusPage = lazyPage(() => import("containers/SpotifyStatus"));
const DataHealthPage = lazyPage(() => import("containers/DataHealth"));
const EndOfYearPage = lazyPage(() => import("containers/EndOfYear"));
const BePlanned = lazyPage(() => import("containers/BePlanned"));
const BePlannedMonthly = lazyPage(() => import("containers/BePlannedMonthly"));
const BePlannedGrid = lazyPage(() => import("containers/BePlannedGrid"));
const Activity = lazyPage(() => import("containers/Activity"));
const OrganicCampaigns = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic")
);
const CreateOrganicCampaign = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoFollow"
  ).then(named("CreateOrganicCampaign"))
);
const CampaignPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/CampaignPage")
);
const CreateCampaign = lazyPage(() =>
  import(
    "components/BeAmplified/Campaigns/Organic/Create/AutoFollow/SharedCreateCampaign"
  ).then(named("CreateCampaign"))
);
const CreateAutoPoster = lazyPage(() =>
  import(
    "components/BeAmplified/Campaigns/Organic/Create/AutoPoster/Twitter"
  ).then(named("CreateAutoPoster"))
);
const AutoPosterCampaignPage = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoPosterCampaignPage"
  )
);
const AutoDmCampaignPage = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoDmCampaignPage"
  )
);
const AutoLikerCampaignPage = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoLikerCampaignPage"
  )
);
const GroupExtractor = lazyPage(() =>
  import(
    "components/BeAmplified/Campaigns/Organic/Create/GroupExtractor/Facebook"
  ).then(named("GroupExtractor"))
);
const CreateAutoDm = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoDm").then(
    named("CreateAutoDm")
  )
);
const CreateAutoLiker = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoLiker"
  ).then(named("CreateAutoLiker"))
);
const CreateAutoCommenter = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/CreateCampaign/AutoCommenter"
  ).then(named("CreateAutoCommenter"))
);
const AutoCommenterCampaignPage = lazyPage(() =>
  import(
    "containers/BeAmplified/Campaigns/Organic/ViewCampaign/AutoCommenterCampaignPage"
  )
);
const CreateScrapper = lazyPage(() =>
  import("containers/BeAmplified/Scrappers/CreateScrapper").then(
    named("CreateScrapper")
  )
);
const CommentsExtractor = lazyPage(() =>
  import("containers/BeAmplified/Scrappers/instagram/CommentsExtractor").then(
    named("CommentsExtractor")
  )
);
const ExtractorPage = lazyPage(() =>
  import("containers/BeAmplified/Campaigns/Organic/ViewCampaign/ExtractorPage")
);
const BePlannedPageV2 = lazyPage(() => import("containers/BeplannedV2"));
const SchedulePost = lazyPage(() =>
  import("containers/SchedulePost/SchedulePost")
);
const ContentPlannerFeed = lazyPage(() =>
  import("containers/BeplannedV2/ContentPlannerData/ContentPlannerFeed").then(
    named("ContentPlannerFeed")
  )
);
const Notifications = lazyPage(() =>
  import("containers/Notifications/Notifications")
);
const ReportV2 = lazyPage(() =>
  import("containers/AiReport/sections/ReportV2")
);

export const AppRouter: FunctionComponent<RouteComponentProps> = () => (
  <ErrorBoundary>
    <ToastContainer />
    <UserProvider>
      <UnsavedProvider>
        <Router>
          <App path="/">
            <AuthPage path="sign-in" />
            <AuthPage path="forgot-password" />
            <Dashboard path="/">
              <PrivateRoute path="/">
                <UserManagementPage path="admin-panel" />
                <AdminSettings path="admin-settings" />
                {/* <MassEmailer path="mass-emailer" /> */}
                <OrganicCampaigns path="campaigns/organic" />
                {/* <EmailCampaignCreation path="mass-emailer/new-campaign" /> */}

                <Redirect
                  from="be-planned-feed"
                  to="/content-plan-feed"
                  noThrow
                />

                <BePlanned path="content-plan-feedv2" />
                <BePlannedPageV2 path="content-plan-feedv3" />
                <ContentPlannerFeed path="content-plan-feed" />
                <ReportV2 path="ai/dashboard" />
                {/* <GeneratedInsight path="ai/insight" /> */}
                <ContentPlannerFeed path="content-plan-feed/:uid/:cemail/:datatype" />
                <SchedulePost path="post-schedule" />
                <Notifications path="notifications" />
                <Redirect
                  from="be-planned-monthly"
                  to="/monthly-conversation-planner"
                  noThrow
                />

                <BePlannedMonthly path="monthly-conversation-planner" />
                <CreateOrganicCampaign path="campaigns/organic/create" />
                <CreateScrapper path="campaigns/scrappers/create" />
                <CreateAutoDm path="campaigns/organic/create/auto-dm" />
                <CreateAutoDm path="campaigns/organic/create/:platform/auto-dm" />
                <CreateAutoDm path="campaigns/organic/create/auto-dm/:uid/edit" />
                <CreateAutoDm path="campaigns/organic/create/:platform/auto-dm/:uid/edit" />
                <CreateAutoPoster path="campaigns/organic/create/auto-poster" />
                <CreateCampaign path="campaigns/organic/:uid/edit" />
                <CreateCampaign path="campaigns/organic/:platform/:uid/edit" />
                <GroupExtractor path="campaigns/organic/create/extractor" />
                <CommentsExtractor path="campaigns/organic/create/comments-extractor" />
                <CreateAutoLiker path="campaigns/organic/create/:platform/auto-liker" />
                <CreateAutoCommenter path="campaigns/organic/create/:platform/auto-commenter" />
                <ClientInfoPage path="client-info/:uid" />
                {/* <CampaignReport path="mass-emailer/:campaignId" /> */}
                <BeMeasured path="be-measured" />
                <HealthCheckPage path="health-check" />
                <SpotifyStatusPage path="spotify-status" />
                <DataHealthPage path="data-health" />
                <EndOfYearPage path="end-of-year" />
                <BePlannedGrid path="be-planned-grid" />
                <Activity path="kit-activity" />
                <Activity path="admin-activity" />
                <OrganicCampaigns path="campaigns/organic" />
                <CampaignPage path="campaigns/organic/:uid" />
                <CampaignPage path="campaigns/organic/:platform/:uid" />
                <AutoPosterCampaignPage path="campaigns/organic/autoposter/:uid" />
                <AutoDmCampaignPage path="campaigns/organic/:platform/auto-dm/:uid" />
                <AutoLikerCampaignPage path="campaigns/organic/auto-liker/:platform/:uid" />
                <AutoCommenterCampaignPage path="campaigns/organic/auto-commenter/:platform/:uid" />
                <ExtractorPage path="campaigns/organic/extractor/:platform/:uid" />
                <NotFound default />
              </PrivateRoute>
            </Dashboard>
            <NotFound default />
          </App>
        </Router>
      </UnsavedProvider>
    </UserProvider>
  </ErrorBoundary>
);
